import { useEffect, useState } from 'react'
import { ChevronRight, Clock, Minus, Plus, X } from 'lucide-react'
import { useI18n } from '../../i18n/useI18n'
import { api } from '../../services/api'
import { showToast } from '../../components/Toast'
import type { AllYouCanPlan, AllYouCanState, MenuItem, Store } from '../../store/useStore'
import { maxPartySize } from '../../lib/partySize'
import PartySizeStepper from '../../components/PartySizeStepper'

/**
 * #1353 顧客側の放題プラン（飲み放題・食べ放題）注文。
 *
 * ## なぜメニューに商品として並べるか
 *
 * セルフオーダーで飲み放題だけ店員を呼ばせるなら、セルフオーダーの意味が薄れる。
 * 実店舗のセルフオーダー端末でも飲み放題はメニューの 1 商品として並ぶのが普通なので
 * それに合わせる。
 *
 * ## なぜ一覧で完結させず、タップして詳細を開くか
 *
 * 一覧の中に人数± と追加ボタンを置くと、**スクロール中の誤タップで数千円が
 * カートに入る**。加えて対象商品を 1 行に詰め込むと品数が増えた店で読めない。
 * この画面のメニュー商品はすべて「タップ→詳細→追加」なので、型もそちらに揃える。
 *
 * ## 注文しても、まだ時間は始まらない
 *
 * 放題プランの注文は**必ず店員承認を挟む**（サーバ側で `pending_approval` を強制）。
 * 承認が制限時間の開始で、そこで店員が人数を確認・訂正する。
 * 4 名卓でも飲み放題は 2 名分、が普通に起きるため、客の申告をそのまま確定にしない。
 *
 * 卓に着いていないと注文できない（サーバも 400 で弾く）ので、席が確定している時だけ出す。
 */

const PLAN_ITEM_PREFIX = 'all-you-can:'

/** 対象商品をアルコール / ソフトドリンク / その他に分ける。
 *
 *  `category` は seed 由来でキー ('drink-alcohol') と日本語ラベル ('アルコール') が
 *  混在しているため、**両方を拾う**。片方だけ見ると分類から漏れて「その他」に落ちる。 */
type DrinkGroup = 'alcohol' | 'soft' | 'other'

const groupOf = (item: MenuItem): DrinkGroup => {
  const c = String(item.category || '')
  if (c.includes('alcohol') || c.includes('アルコール')) return 'alcohol'
  if (c.includes('soft') || c.includes('ソフト')) return 'soft'
  return 'other'
}

type Props = {
  store: Store
  /** 着席中の卓。無い場合は表示しない（卓なしでは注文できないため） */
  tableNumber?: string
  /** すでにこの卓でプランが動いているか（二重注文を防ぐ） */
  /** #1583 卓に入るとき客が申告した人数。放題の人数の初期値に使う。
   *  固定 2 だと 130 名の卓で 128 回 + を押すことになる。 */
  seatedPartySize?: number
  planActive?: boolean
  /** 利用中プランの状態。店員が人数を訂正したとき客が反映を確認できるように出す */
  activeState?: AllYouCanState | null
  /** #1587 第 3 引数はこのプランが 0 円にする商品 id。カートに入れた時点では
   *  卓に未適用なので、表示を請求額に合わせるために呼び出し側へ渡す。 */
  onAdd: (item: MenuItem, quantity: number, coveredItemIds?: string[]) => void
}

const hhmm = (iso?: string) => {
  if (!iso) return ''
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export default function AllYouCanSection({
  store, tableNumber, seatedPartySize, planActive, activeState, onAdd,
}: Props) {
  const { t } = useI18n()
  const [plans, setPlans] = useState<AllYouCanPlan[]>([])
  const [selected, setSelected] = useState<AllYouCanPlan | null>(null)

  useEffect(() => {
    let cancelled = false
    api.listAllYouCanPlans(store.id)
      .then((r) => { if (!cancelled) setPlans(r.plans) })
      .catch((e) => console.error('[AllYouCanSection] load failed', e))
    return () => { cancelled = true }
  }, [store.id])

  // #2039 時間制プランは客が自分で頼めない (時計の起点は店員が卓に付けた瞬間)。
  // 一覧には出さず、適用中の状態だけを見せる。
  const orderable = plans.filter((p) => p.pricing !== 'time')
  const time = activeState?.timeCharge ?? null

  if (!tableNumber) return null

  // #2039 時間料金。**今いくらか**と**次の単位に入る時刻**を客がいつでも確かめられる
  // ようにする (店員に聞かないと分からない、をなくす)。時計を止めた後 (会計中) も
  // 確定した金額を出し続ける。
  if (time && activeState) {
    const running = plans.find((p) => p.id === activeState.planId)
    const hasDrinks = (activeState.itemIds?.length ?? 0) > 0
    return (
      <section className="px-4 pt-3">
        <div
          className={`rounded-xl border px-3 py-2.5 ${
            time.running ? 'border-matcha/20 bg-matcha/10' : 'border-amber-200 bg-amber-50'
          }`}
        >
          <p className={`font-body text-xs font-semibold ${time.running ? 'text-matcha' : 'text-amber-800'}`}>
            {activeState.name}
          </p>
          <p className="mt-0.5 font-display text-lg font-bold text-text-primary">
            {t(time.running ? 'allYouCan.timeNow' : 'allYouCan.timeFixed', {
              total: time.amountYen.toLocaleString(),
            })}
          </p>
          <p className="mt-0.5 font-body text-[11px] text-text-secondary">
            {t('allYouCan.timeParty', { count: String(time.chargedPartySize) })}
            {time.freePartySize > 0 && (
              <> ・ {t('allYouCan.timeFree', { count: String(time.freePartySize) })}</>
            )}
          </p>
          {time.running && (
            <p className="mt-0.5 font-body text-[11px] text-text-secondary">
              {time.nextUnitAt
                ? t('allYouCan.timeNext', {
                  unit: String(time.unitMinutes), time: hhmm(time.nextUnitAt),
                })
                : t('allYouCan.timeCapped')}
            </p>
          )}
          {time.running && hasDrinks && running && (
            <button
              onClick={() => setSelected(running)}
              className="mt-2 inline-flex items-center gap-1 font-body text-xs font-semibold text-matcha tap-effect"
            >
              {t('allYouCan.openOrder')}
              <ChevronRight size={14} />
            </button>
          )}
        </div>
        {selected && (
          <PlanOrderModal
            plan={selected}
            store={store}
            state={activeState}
            onClose={() => setSelected(null)}
            onAdd={onAdd}
          />
        )}
      </section>
    )
  }

  if (orderable.length === 0) return null

  // #2037 適用中は**この枠を畳む**。一度頼んだらもう注文するものではないのに、プランのカードが
  // メニューの先頭に残り、メニューを見るたびに越えてスクロールすることになっていた。
  // 残り時間と人数は客が気にするので消さず、1 枠だけにする。押せば今までどおり対象の
  // ドリンクを頼む画面が開く (適用中のプランで開く)。
  if (planActive && activeState) {
    const running = plans.find((p) => p.id === activeState.planId) ?? orderable[0]
    return (
      <section className="px-4 pt-3">
        <button
          onClick={() => setSelected(running)}
          className="flex w-full items-center gap-2 rounded-xl border border-matcha/20 bg-matcha/10 px-3 py-2.5 text-left tap-effect"
        >
          <div className="min-w-0 flex-1">
            <p className="font-body text-xs font-semibold text-matcha">
              {t('allYouCan.activeNow', {
                name: activeState.name,
                count: String(activeState.partySize),
              })}
            </p>
            <p className="mt-0.5 font-body text-[11px] text-text-secondary">
              {t('allYouCan.activeRemaining', {
                count: String(activeState.remainingMinutes ?? 0),
                lastOrder: hhmm(activeState.lastOrderAt),
              })}
            </p>
          </div>
          <ChevronRight size={16} className="shrink-0 text-matcha" />
        </button>
        {selected && (
          <PlanOrderModal
            plan={selected}
            store={store}
            state={activeState}
            onClose={() => setSelected(null)}
            onAdd={onAdd}
          />
        )}
      </section>
    )
  }

  return (
    <section className="px-4 pt-3">
      <h3 className="font-display text-sm font-bold text-text-primary mb-2">
        {t('allYouCan.sectionTitle')}
      </h3>

      <div className="space-y-2">
        {orderable.map((p) => (
          <PlanRow
            key={p.id}
            plan={p}
            store={store}
            active={!!planActive}
            onOpen={() => setSelected(p)}
          />
        ))}
      </div>

      {/* 利用前と利用中でモーダルの役割が変わる。
          - 利用前: 「何が頼めるか」を見て、プランを注文するか決める
          - 利用中: プランはもう始まっているので、**対象ドリンクを選んで注文する**画面
          同じ内容を出すと、利用中の客は「もう一度プランを買うのか」と迷う。 */}
      {selected && (planActive ? (
        <PlanOrderModal
          plan={selected}
          store={store}
          state={activeState ?? null}
          onClose={() => setSelected(null)}
          onAdd={onAdd}
        />
      ) : (
        <PlanDetailModal
          plan={selected}
          store={store}
          seatedPartySize={seatedPartySize}
          onClose={() => setSelected(null)}
          // #1587 `covered` (このプランが 0 円にする商品) を落とさないこと。
          // 落とすとカートが満額のまま表示され、承認後の請求額とズレる。
          onAdd={(item, qty, covered) => {
            onAdd(item, qty, covered)
            setSelected(null)
          }}
        />
      ))}
    </section>
  )
}

/** 一覧の行。**注文操作は持たない** — タップで詳細を開くだけ。
 *
 *  利用中でも無効化しない。「何が飲み放題だったか」は利用中こそ確認したいし、
 *  灰色で押せないカードは理由が見えず**壊れているように見える**。
 *  追加できないことは詳細側で理由付きで伝える。 */
function PlanRow({
  plan, store, active, onOpen,
}: {
  plan: AllYouCanPlan
  store: Store
  /** 利用中か。押した先が「注文する」か「内容を見る」かで文言を変える */
  active: boolean
  onOpen: () => void
}) {
  const { t } = useI18n()
  const items = itemsOf(plan, store)
  const counts = {
    soft: items.filter((i) => groupOf(i) === 'soft').length,
    alcohol: items.filter((i) => groupOf(i) === 'alcohol').length,
  }

  return (
    <button
      onClick={onOpen}
      className="w-full text-left rounded-xl border border-gray-100 p-3 tap-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-body text-sm font-semibold text-text-primary">{plan.name}</p>
          <p className="font-display text-sm font-bold text-yokoso-red mt-0.5">
            ¥{plan.priceYen.toLocaleString()}
            <span className="font-body text-xs text-text-secondary ml-1">
              {t('allYouCan.perPerson')}
            </span>
          </p>
          {/* 何が何品飲めるのかを一覧の時点で伝える。品名の羅列より判断しやすい。 */}
          <p className="mt-1 font-body text-[11px] text-text-tertiary">
            {[
              counts.soft > 0 ? t('allYouCan.softCount', { count: String(counts.soft) }) : '',
              counts.alcohol > 0 ? t('allYouCan.alcoholCount', { count: String(counts.alcohol) }) : '',
            ].filter(Boolean).join(' ・ ')}
          </p>
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 font-body text-[11px] text-text-secondary">
          <Clock size={11} />
          {t('allYouCan.minutes', { count: String(plan.durationMinutes) })}
        </span>
      </div>

      {/* 押せることが**見た目で分かる**ようにカード内にボタン行を置く。
          枠線と ">" だけだと、すぐ上の「ご利用中」パネル（押せない）と同じ見た目で
          静的な情報カードにしか見えない。押した先が何かも文言で示す。 */}
      <div className="mt-2.5 flex items-center justify-center gap-1 rounded-lg bg-matcha/10 py-2 font-body text-xs font-semibold text-matcha">
        {active ? t('allYouCan.openOrder') : t('allYouCan.openDetail')}
        <ChevronRight size={14} />
      </div>
    </button>
  )
}

/** 詳細＋注文。対象商品をソフトドリンク / アルコールに分けて出す。 */
function PlanDetailModal({
  plan, store, seatedPartySize, onClose, onAdd,
}: {
  plan: AllYouCanPlan
  store: Store
  seatedPartySize?: number
  onClose: () => void
  /** #1587 第 3 引数はこのプランが 0 円にする商品 id。カートに入れた時点では
   *  卓に未適用なので、表示を請求額に合わせるために呼び出し側へ渡す。 */
  onAdd: (item: MenuItem, quantity: number, coveredItemIds?: string[]) => void
}) {
  const { t } = useI18n()
  // #1583 入店時に申告した人数を初期値にする。固定 2 だと 130 名の卓で
  // 128 回 + を押すことになる。上限は店舗設定 (1 組の最大人数)。
  const [party, setParty] = useState(
    Math.min(Math.max(1, seatedPartySize ?? 2), maxPartySize(store)),
  )
  const items = itemsOf(plan, store)

  const groups: { key: DrinkGroup; labelKey: string }[] = [
    { key: 'soft', labelKey: 'allYouCan.groupSoft' },
    { key: 'alcohol', labelKey: 'allYouCan.groupAlcohol' },
    { key: 'other', labelKey: 'allYouCan.groupOther' },
  ]

  const add = () => {
    onAdd(
      {
        // サーバはこの prefix で放題プランの注文だと判別する。
        // 価格・名称はサーバがプラン定義から引き直すので、ここの値は表示用。
        id: `${PLAN_ITEM_PREFIX}${plan.id}`,
        name: plan.name,
        // `nameEn` はサーバの CartItem で**必須**。未定義だと 422 で注文が丸ごと落ちる。
        // 厨房伝票は ja/en を別行に印字するので、和名のフォールバックは入れず空にする。
        nameEn: '',
        price: plan.priceYen,
        category: 'drink-alcohol',
      } as MenuItem,
      party,
      plan.itemIds,
    )
    showToast({ type: 'success', message: t('allYouCan.addedToCart') })
  }

  return (
    <div
      className="fixed inset-0 z-90 flex items-end justify-center bg-black/50 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-107.5 bg-white rounded-t-2xl p-5 pb-8 animate-slide-up max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
          aria-label={t('common.close')}
        >
          <X size={18} className="text-text-secondary" />
        </button>

        <h3 className="font-display font-bold text-lg text-text-primary pr-10">{plan.name}</h3>
        <p className="font-display text-xl font-bold text-yokoso-red mt-1">
          ¥{plan.priceYen.toLocaleString()}
          <span className="font-body text-xs text-text-secondary ml-1">
            {t('allYouCan.perPerson')}
          </span>
          <span className="ml-3 inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 font-body text-[11px] font-normal text-text-secondary align-middle">
            <Clock size={11} />
            {t('allYouCan.minutes', { count: String(plan.durationMinutes) })}
          </span>
        </p>

        {plan.description && (
          <p className="mt-2 font-body text-xs text-text-secondary whitespace-pre-wrap">
            {plan.description}
          </p>
        )}

        {/* 何が飲めるのかが選ぶ判断そのもの。ソフトドリンクとアルコールを分けて出す。 */}
        <div className="mt-4 space-y-3">
          {groups.map(({ key, labelKey }) => {
            const list = items.filter((i) => groupOf(i) === key)
            if (list.length === 0) return null
            return (
              <div key={key}>
                <p className="font-body text-xs font-semibold text-text-secondary">
                  {t(labelKey)}
                  <span className="ml-1 font-normal text-text-tertiary">({list.length})</span>
                </p>
                <ul className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1">
                  {list.map((i) => (
                    <li key={i.id} className="font-body text-xs text-text-primary truncate">
                      {i.name}
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>

        {/* 承認まで時間が始まらないことを先に伝える。「押した瞬間に 2 時間が動く」と
            誤解されると、客はタップをためらう。 */}
        <p className="mt-4 rounded-lg bg-gray-50 px-3 py-2 font-body text-[11px] text-text-secondary">
          {t('allYouCan.startsAfterApproval')}
        </p>

        <div className="mt-4 flex items-center justify-between">
          <span className="font-body text-sm text-text-secondary">{t('allYouCan.party')}</span>
          {/* REQ-28 (#1536) 上限は店舗設定 (既定 50)。
            * #1583 ± だけだと 130 名で 128 回タップになるので直接入力も持たせる。 */}
          <PartySizeStepper
            value={party}
            max={maxPartySize(store)}
            onChange={setParty}
          />
        </div>

        <button
          onClick={add}
          className="w-full mt-4 h-12 rounded-xl bg-matcha text-white font-display font-bold text-sm tap-effect"
        >
          ¥{(plan.priceYen * party).toLocaleString()} {t('allYouCan.addCta')}
        </button>
      </div>
    </div>
  )
}


/** 利用中の注文モーダル。**対象ドリンクを選んで頼む**画面。
 *
 *  利用前と同じ「プランを買う」画面を出すと、既に始まっている客は
 *  「もう一度買うのか」と迷う。利用中に客がしたいことは 1 つ —
 *  **飲み放題のドリンクを頼むこと**なので、そこだけができる形にする。
 *
 *  価格は全部 0 円で出す（サーバも 0 円で確定する）。ラストオーダーを過ぎたら
 *  注文させない — サーバも `all_you_can_last_order_passed` で弾く。
 */
function PlanOrderModal({
  plan, store, state, onClose, onAdd,
}: {
  plan: AllYouCanPlan
  store: Store
  state: AllYouCanState | null
  onClose: () => void
  /** #1587 第 3 引数はこのプランが 0 円にする商品 id。カートに入れた時点では
   *  卓に未適用なので、表示を請求額に合わせるために呼び出し側へ渡す。 */
  onAdd: (item: MenuItem, quantity: number, coveredItemIds?: string[]) => void
}) {
  const { t } = useI18n()
  const items = itemsOf(plan, store)
  const [qty, setQty] = useState<Record<string, number>>({})

  const closed = !!state?.lastOrderPassed
  const totalCount = Object.values(qty).reduce((n, v) => n + v, 0)
  const bump = (id: string, d: number) =>
    setQty((q) => ({ ...q, [id]: Math.max(0, Math.min(20, (q[id] || 0) + d)) }))

  const groups: { key: DrinkGroup; labelKey: string }[] = [
    { key: 'soft', labelKey: 'allYouCan.groupSoft' },
    { key: 'alcohol', labelKey: 'allYouCan.groupAlcohol' },
    { key: 'other', labelKey: 'allYouCan.groupOther' },
  ]

  const submit = () => {
    // 選んだ分をまとめてカートへ。1 杯ずつ閉じ開きさせない（宴会中に何度も開くのは苦痛）。
    for (const it of items) {
      const n = qty[it.id] || 0
      if (n > 0) onAdd(it, n)
    }
    showToast({ type: 'success', message: t('allYouCan.addedToCart') })
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-90 flex items-end justify-center bg-black/50 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-107.5 bg-white rounded-t-2xl animate-slide-up max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
            aria-label={t('common.close')}
          >
            <X size={18} className="text-text-secondary" />
          </button>
          <h3 className="font-display font-bold text-lg text-text-primary pr-10">
            {t('allYouCan.orderTitle')}
          </h3>
          {/* 残り時間とラストオーダーは注文するかの判断に直結するので常に見せる。
              #2039 時間で課金するプランには残り時間もラストオーダーも無い (居た分だけ掛かる)。
              そのまま出すと「残り 698 分・ラストオーダー 09:30」と上限の時刻が出てしまう。 */}
          {state && !state.timeCharge && (
            <p className="mt-1 font-body text-xs text-text-secondary">
              {closed
                ? t('allYouCan.lastOrderPassed')
                : t('allYouCan.activeRemaining', {
                  count: String(state.remainingMinutes ?? 0),
                  lastOrder: hhmm(state.lastOrderAt),
                })}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4">
          {groups.map(({ key, labelKey }) => {
            const list = items.filter((i) => groupOf(i) === key)
            if (list.length === 0) return null
            return (
              <div key={key}>
                <p className="font-body text-xs font-semibold text-text-secondary mb-1.5">
                  {t(labelKey)}
                  <span className="ml-1 font-normal text-text-tertiary">({list.length})</span>
                </p>
                <div className="space-y-1.5">
                  {list.map((i) => (
                    <div key={i.id} className="flex items-center justify-between gap-2 rounded-xl border border-gray-100 px-3 py-2">
                      <div className="min-w-0">
                        <p className="font-body text-sm text-text-primary truncate">{i.name}</p>
                        {/* 0 円で頼めることを 1 品ずつ明示する。通常価格の記憶と食い違うため */}
                        <p className="font-body text-[11px] text-matcha">
                          ¥0
                          <span className="ml-1 text-text-tertiary line-through">
                            ¥{i.price.toLocaleString()}
                          </span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => bump(i.id, -1)}
                          disabled={closed || !(qty[i.id] > 0)}
                          aria-label={t('allYouCan.decrease')}
                          className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center tap-effect disabled:opacity-25"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-5 text-center font-display text-sm font-bold">
                          {qty[i.id] || 0}
                        </span>
                        <button
                          onClick={() => bump(i.id, 1)}
                          disabled={closed}
                          aria-label={t('allYouCan.increase')}
                          className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center tap-effect disabled:opacity-25"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 safe-bottom">
          <button
            onClick={submit}
            disabled={closed || totalCount === 0}
            className="w-full h-12 rounded-xl bg-matcha text-white font-display font-bold text-sm tap-effect disabled:opacity-40"
          >
            {closed
              ? t('allYouCan.lastOrderPassed')
              : t('allYouCan.addSelected', { count: String(totalCount) })}
          </button>
        </div>
      </div>
    </div>
  )
}

/** プランに含まれるメニュー item を引く。削除済み id は落とす。 */
function itemsOf(plan: AllYouCanPlan, store: Store): MenuItem[] {
  const byId = new Map((store.menu || []).map((m) => [m.id, m]))
  return (plan.itemIds || [])
    .map((id) => byId.get(id))
    .filter((m): m is MenuItem => !!m)
}
